import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { PageHeader, Card, Empty } from "../components/UI";
import { usePagination, PaginationBar } from "../components/Pagination";
import { Search } from "lucide-react";
import { toast } from "sonner";

// Simple flat "Case Load Report" table. One row per active client-clinician
// assignment. Deliberately basic so front desk and clinical directors can share
// the same worksheet without training. See `signatureAuditHtml` for the
// tamper-evident universal signature block used elsewhere in the app.

function CompletedPill({ value }) {
  const tone = value === "Yes"
    ? "bg-[#E8F5E9] text-[#2E7D32]"
    : value === "No"
      ? "bg-[#FFEBEE] text-[#C62828]"
      : "bg-[#F5F2EB] text-[#7B6157]";
  return <span className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${tone}`}>{value}</span>;
}

function ClientCell({ row, onClick }) {
  return (
    <button type="button" onClick={onClick}
      data-testid={`caseload-client-${row.client_id}`}
      className="flex items-center gap-2 text-left transition-colors hover:text-[#4E342E]">
      {row.client_photo
        ? <img src={row.client_photo} alt="" className="h-7 w-7 rounded-full object-cover" />
        : <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#F5F2EB] text-[10px] font-bold text-[#4E342E]">{row.client_initials}</span>}
      <span className="truncate text-sm text-[#3E2723]">{row.client}</span>
    </button>
  );
}

const fmt = (d) => (d ? dayjs(d).format("MM/DD/YYYY") : "-");

export default function Caseload() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/caseload/report")
      .then((r) => setRows(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, []);
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) =>
      r.provider.toLowerCase().includes(term)
      || r.client.toLowerCase().includes(term)
      || (r.intervention || "").toLowerCase().includes(term));
  }, [rows, q]);
  const pg = usePagination(filtered);

  return (
    <div>
      <PageHeader title="Caseload" subtitle="Active clients, assigned interventions, and provider transitions" />
      <Card className="p-0 overflow-hidden" data-testid="caseload-report-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#EFEBE0] bg-[#FDFCF8] px-5 py-4">
          <h2 className="font-heading text-lg font-bold text-[#3E2723]">Case Load Report</h2>
          <div className="relative w-full max-w-xs">
            <Search size={14} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[#84675B]" />
            <input data-testid="caseload-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search here"
              className="h-10 w-full rounded-full border border-[#E5DFD3] bg-white pl-9 pr-4 text-sm text-[#3E2723] focus:border-[#8D6E63] focus:outline-none focus:ring-1 focus:ring-[#8D6E63]" />
          </div>
        </div>
        {loading ? (
          <div className="py-16 text-center text-sm text-[#84675B]">Loading caseload report...</div>
        ) : filtered.length === 0 ? (
          <Empty text={q ? "No matches, try a different search term" : "No active caseload assignments"} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#F5EFE1] text-left">
                    <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-[#8A5A00]">Provider</th>
                    <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-[#8A5A00]">Client</th>
                    <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-[#8A5A00]">Date Assigned</th>
                    <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-[#8A5A00]">Intervention Assigned</th>
                    <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-[#8A5A00]">Intervention Completed</th>
                    <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-[#8A5A00]">Date Provider Removed from Treatment</th>
                  </tr>
                </thead>
                <tbody>
                  {pg.pageRows.map((r) => (
                    <tr key={r.id} data-testid={`caseload-row-${r.client_id}`} className="border-b border-[#EFEBE0] last:border-0 hover:bg-[#FDFCF8]">
                      <td className="px-5 py-3 text-[#3E2723]">{r.provider}</td>
                      <td className="px-5 py-3"><ClientCell row={r} onClick={() => navigate(`/clients/${r.client_id}`)} /></td>
                      <td className="px-5 py-3 tabular-nums text-[#5D4037]">{fmt(r.date_assigned)}</td>
                      <td className="px-5 py-3 text-[#5D4037] max-w-[260px] truncate" title={r.intervention}>{r.intervention}</td>
                      <td className="px-5 py-3"><CompletedPill value={r.completed || "N/A"} /></td>
                      <td className="px-5 py-3 tabular-nums text-[#5D4037]">{r.date_removed ? fmt(r.date_removed) : <span className="text-[#84675B]">N/A</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationBar {...pg} testId="caseload-pagination" />
          </>
        )}
      </Card>
    </div>
  );
}
